import { log } from "../log";
import { useCallback, useEffect, useRef, useState } from "react";
import useReactUseWebSocket, { type Options } from "react-use-websocket";
import { WS_AUTH_SUBPROTOCOL } from "./websocket.utils";

export { WS_AUTH_SUBPROTOCOL };

export type UseWebSocketClientParams = Options & {
  /** When true, the socket is closed and reconnect is suppressed until false. */
  idle?: boolean;
  baseUrl: string;
  accessToken?: string | null;
  /** Called when the socket is closed due to idle. */
  onCloseDueToIdle?: () => void;
  /** When set, counts closes and blocks reconnect after this many disconnects. */
  maxDisconnections?: number;
  /** Called when the socket is closed due to token expiry. */
  onCloseDueToTokenExpiry?: () => void;
};

/**
 * Chat stream WebSocket (`/v1/ws/stream`) shared by web and mobile.
 */
export function useWebSocketClient({
  idle = false,
  onOpen,
  onClose,
  baseUrl,
  accessToken,
  onCloseDueToIdle,
  maxDisconnections,
  onCloseDueToTokenExpiry,
  ...options
}: UseWebSocketClientParams) {
  const [reconnectKey, setReconnectKey] = useState(0);
  const [disconnections, setDisconnections] = useState(0);

  const prevIdleRef = useRef(idle);

  const trackDisconnections =
    maxDisconnections != null && maxDisconnections > 0;
  const hasReachedMaxDisconnections = trackDisconnections
    ? disconnections >= maxDisconnections!
    : false;

  // The single gate for opening a socket. Idle wins over everything else:
  // while the user is away nothing may connect, not a reconnect after a close
  // and not a rotated token. Every path below reads this instead of deciding
  // for itself.
  const canConnect =
    Boolean(accessToken) && !idle && !hasReachedMaxDisconnections;

  // The JWT no longer rides the URL — it travels in the Sec-WebSocket-Protocol
  // subprotocol (see WS_AUTH_SUBPROTOCOL below). The _reconnect nonce stays so
  // bumping it still forces a fresh connection.
  const webSocketUrl =
    accessToken != null && accessToken !== ""
      ? `${baseUrl}/v1/ws/stream?_reconnect=${reconnectKey}`
      : null;

  const shouldReconnect = useCallback(
    (_event: WebSocketEventMap["close"]) => canConnect,
    [canConnect],
  );

  const websocket = useReactUseWebSocket(
    webSocketUrl,
    {
      ...options,
      // Pass the JWT as the second subprotocol. The browser sends it as
      // `Sec-WebSocket-Protocol: edge-ws-v1, <jwt>` during the handshake.
      protocols: accessToken ? [WS_AUTH_SUBPROTOCOL, accessToken] : undefined,
      onOpen: (event) => {
        log("websocket_connected", { reconnects: reconnectKey });
        if (trackDisconnections) {
          setDisconnections(0);
        }
        onOpen?.(event);
      },
      onClose: (event) => {
        log("websocket_disconnected", {
          level: event.wasClean ? "log" : "warning",
          closeCode: event.code,
          reason: event.reason || "server sent no reason",
          wasClean: event.wasClean,
        });
        if (trackDisconnections) {
          setDisconnections((prev) => {
            const newCount = prev + 1;
            if (newCount >= maxDisconnections!) {
              log.error(new Error("WebSocket max disconnections reached"), {
                tags: {
                  error_type: "websocket",
                  error_code: "MAX_DISCONNECTIONS",
                },
                extra: {
                  wasClean: event.wasClean,
                  closeCode: event.code,
                  closeReason: event.reason,
                  disconnections: newCount,
                  maxDisconnections,
                },
              });
            }
            return newCount;
          });
        }
        onClose?.(event);
      },
      shouldReconnect,
    },
    Boolean(accessToken),
  );

  // ===========================================================================
  // Idle user management
  // ===========================================================================
  // Effect: Close when idle
  useEffect(() => {
    if (idle) {
      log("websocket_close_requested", { reason: "user went idle" });

      const ws = websocket.getWebSocket();
      ws?.close(1000, "User is idle");

      onCloseDueToIdle?.();
    }
  }, [idle, websocket, onCloseDueToIdle]);

  // Effect: Reconnect when user becomes active
  useEffect(() => {
    const wasIdle = prevIdleRef.current;
    const isNowActive = !idle;
    prevIdleRef.current = idle;

    if (wasIdle && isNowActive) {
      const ws = websocket.getWebSocket();
      const isClosed = ws?.readyState === WebSocket.CLOSED;

      if (isClosed && canConnect) {
        log("websocket_reconnect_requested", {
          reason: "user became active again",
        });
        queueMicrotask(() => {
          setReconnectKey((prev) => prev + 1);
        });
      }
    }
  }, [idle, canConnect, websocket]);

  // Effect: Close when token expires
  useEffect(() => {
    if (!accessToken) {
      const ws = websocket.getWebSocket();
      ws?.close(1000, "Token expired");
      onCloseDueToTokenExpiry?.();
    }
  }, [accessToken, websocket, onCloseDueToTokenExpiry]);

  // ===========================================================================
  // Page teardown
  // ===========================================================================
  // Effect: close cleanly when the page goes away.
  //
  // Without this, closing the tab tears down TCP with no close frame, and the
  // backend only learns the client is gone when a queued outbound frame fails
  // with a broken pipe. Sending a real 1000 close lets the server cancel the
  // stream's write pump before it attempts a doomed write.
  //
  // pagehide rather than beforeunload: it fires reliably on tab close and on
  // mobile Safari, where beforeunload often does not.
  //
  // Only when the page is being discarded (`persisted === false`). A page
  // entering the back/forward cache (`persisted === true`) may be restored, and
  // the browser suspends its sockets for us — closing here would drop a socket
  // the resume path expects, and the existing close/reconnect handling already
  // covers the return trip.
  useEffect(() => {
    // Feature-detect the API, not just the name: React Native aliases `window`
    // to the JS global, so the typeof check alone passes there while
    // addEventListener may not exist (TypeError on mount) — and pagehide never
    // fires outside a browser anyway. The mobile app consumes this hook via
    // WebSocketProvider, so this must be a clean no-op on RN.
    if (
      typeof window === "undefined" ||
      typeof window.addEventListener !== "function"
    )
      return;

    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      const ws = websocket.getWebSocket();
      ws?.close(1000, "Page hidden");
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [websocket]);

  // Effect: Reconnect when the token rotates. The token now lives in the
  // subprotocol, not the URL, and react-use-websocket only reconnects on URL /
  // connect / queryParams changes — so a refreshed JWT would otherwise keep
  // using the old socket. Bumping the reconnect key forces a new handshake
  // that carries the fresh token.
  //
  // Bumping the key is an initial connect, not a reconnect, so shouldReconnect
  // never sees it — that is how rotations used to open sockets on an idle page
  // and kill them mid-handshake with a 1006. The rotation is always recorded
  // so it is only ever acted on once, but while the gate is shut the fresh
  // token just waits in the subprotocol options and the wake-up effect above
  // spends it on a single connection.
  const prevTokenRef = useRef(accessToken);
  useEffect(() => {
    const prev = prevTokenRef.current;
    prevTokenRef.current = accessToken;
    if (!canConnect) return;
    if (prev && prev !== accessToken) {
      log("websocket_reconnect_requested", {
        reason: "access token rotated",
      });
      setReconnectKey((prevKey) => prevKey + 1);
    }
  }, [accessToken, canConnect]);

  // Allow manual reconnection by incrementing the reconnect key which forces a new connection.
  const reconnect = useCallback(() => {
    log("websocket_reconnect_requested", {
      reason: "caller asked for a reconnect",
    });
    if (trackDisconnections) {
      setDisconnections(0);
    }
    setReconnectKey((prev) => prev + 1);
  }, [trackDisconnections]);

  return Object.assign({}, websocket, {
    reconnect,
    hasReachedMaxDisconnections,
  });
}
